import { useEffect, useState } from 'react'

// Shortname de la cuenta de Disqus (Disqus → Settings → General).
const DISQUS_SHORTNAME = 'alinova-blog'

interface DisqusPageConfig {
  url: string
  identifier: string
  title: string
}

interface DisqusGlobal {
  reset: (options: { reload: boolean; config: () => void }) => void
}

interface UseDisqusThreadOptions {
  /** Identificador único del hilo (por artículo, por sección, etc). */
  identifier: string
  /** Título mostrado en el panel de moderación de Disqus. */
  title: string
  /** Permite desactivar la carga (por ejemplo, mientras no hay shortname configurado). */
  enabled?: boolean
}

/**
 * Carga (o reinicia) un hilo de comentarios de Disqus con una configuración de
 * página específica, y captura cualquier error de carga del script externo
 * para que nunca se propague al resto de la aplicación.
 */
export function useDisqusThread({
  identifier,
  title,
  enabled = true,
}: UseDisqusThreadOptions) {
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    if (!enabled) return

    setLoadError(false)
    const pageUrl = window.location.href

    const applyConfig = function (this: { page: DisqusPageConfig }) {
      this.page.url = pageUrl
      this.page.identifier = identifier
      this.page.title = title
    }

    // Cualquier excepción lanzada por el script de terceros durante la carga
    // se captura aquí para no propagarse al resto de la app.
    const handleWindowError = (event: ErrorEvent) => {
      if (event.filename && event.filename.includes('disqus.com')) {
        event.preventDefault()
        setLoadError(true)
      }
    }
    window.addEventListener('error', handleWindowError)

    let script: HTMLScriptElement | null = null

    try {
      ;(window as unknown as Record<string, unknown>).disqus_config = applyConfig

      const disqusGlobal = (window as unknown as { DISQUS?: DisqusGlobal }).DISQUS

      if (disqusGlobal) {
        // Disqus ya está cargado (p. ej. al navegar entre artículos): solo
        // reiniciamos el hilo con la nueva configuración de página.
        disqusGlobal.reset({ reload: true, config: applyConfig })
      } else {
        script = document.createElement('script')
        script.src = `https://${DISQUS_SHORTNAME}.disqus.com/embed.js`
        script.setAttribute('data-timestamp', String(+new Date()))
        script.async = true
        script.onerror = () => setLoadError(true)
        document.body.appendChild(script)
      }
    } catch {
      setLoadError(true)
    }

    const hideReactionEmojis = window.setInterval(() => {
      const reactions = document.querySelectorAll<HTMLElement>(
        '#disqus_thread .dsq-reaction, #disqus_thread .dsq-reaction-button, #disqus_thread .dsq-reaction-count',
      )
      if (reactions.length > 0) {
        reactions.forEach((node) => {
          node.style.display = 'none'
        })
        window.clearInterval(hideReactionEmojis)
      }
    }, 1000)

    return () => {
      window.removeEventListener('error', handleWindowError)
      window.clearInterval(hideReactionEmojis)
      script?.remove()
    }
  }, [identifier, title, enabled])

  return { loadError }
}
